import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Home } from "lucide-react";
import { GoogleMapView } from "@/components/GoogleMapView";
import { MapView } from "@/components/MapView";
import { Legend } from "@/components/Legend";
import { AIPanel } from "@/components/AIPanel";
import { HotSpots } from "@/components/HotSpots";
import { BasemapToggle } from "@/components/BasemapToggle";
import { DroneDropChip } from "@/components/DroneDropChip";
import { LiveDroneCounter } from "@/components/LiveDroneCounter";
import { useChatStream } from "@/hooks/useChatStream";
import { useAssessment } from "@/hooks/useAssessment";
import { CITIES } from "@/data/cities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [params] = useSearchParams();
  const [zones, setZones] = useState(null);
  const [spots, setSpots] = useState([]);
  const [weightClass, setWeightClass] = useState("sub250");
  const [input, setInput] = useState("");
  const [sheetOpen, setSheetOpen] = useState(true);
  const [basemap, setBasemap] = useState("satellite");
  const [showDanger, setShowDanger] = useState(false);
  const [gmapFailed, setGmapFailed] = useState(false);

  useEffect(() => {
    window.gm_authFailure = () => setGmapFailed(true);
    return () => { delete window.gm_authFailure; };
  }, []);
  const weightRef = useRef(weightClass);
  weightRef.current = weightClass;
  const mapRef = useRef(null);
  const { messages, streaming, streamChat } = useChatStream(weightRef);
  const openSheet = useCallback(() => setSheetOpen(true), []);
  const {
    assessment, selectedPoint, flyTarget, setFlyTarget, locating,
    onMapClick, onSearchSelect, onSpotSelect, onLocate, onDroneDrop,
  } = useAssessment({ weightRef, streamChat, streaming, onAssessStart: openSheet });

  const filteredZones = useMemo(() => {
    if (!zones) return null;
    if (showDanger) return zones;
    return { ...zones, features: zones.features.filter((f) => !["DANGER", "HIRTA"].includes(f.properties.zone_type)) };
  }, [zones, showDanger]);

  const onDropAt = useCallback((x, y) => {
    const m = mapRef.current;
    if (!m) return;
    const ll = m.toLatLng ? m.toLatLng(x, y) : m.mouseEventToLatLng({ clientX: x, clientY: y });
    if (ll) onDroneDrop(ll.lat, ll.lng);
  }, [onDroneDrop]);

  useEffect(() => {
    axios.get(`${API}/zones`).then((r) => setZones(r.data)).catch(() => toast.error("Failed to load airspace zones"));
    axios.get(`${API}/spots`).then((r) => setSpots(r.data)).catch(() => {});
    document.title = "Where Can I Fly My Drone? | UK CAA Zone Map & AI Copilot";
  }, []);

  useEffect(() => {
    const lat = parseFloat(params.get("lat")), lng = parseFloat(params.get("lng"));
    if (!isNaN(lat) && !isNaN(lng)) setFlyTarget({ lat, lng, zoom: parseInt(params.get("zoom")) || 11 });
  }, [params, setFlyTarget]);

  const onSend = useCallback((text) => {
    if (!text.trim() || streaming) return;
    setInput("");
    streamChat(text.trim(), assessment?.context);
  }, [streaming, streamChat, assessment]);

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[#0a0a0a]">
      {gmapFailed ? (
        <MapView zones={filteredZones} zonesKey={`z-${showDanger}`} onMapClick={onMapClick} selectedPoint={selectedPoint} flyTarget={flyTarget} status={assessment?.status} weightClass={weightClass} spots={spots} onSpotSelect={onSpotSelect} basemap={basemap} mapRef={mapRef} />
      ) : (
        <GoogleMapView zones={filteredZones} zonesKey={`z-${showDanger}`} onMapClick={onMapClick} selectedPoint={selectedPoint} flyTarget={flyTarget} status={assessment?.status} weightClass={weightClass} spots={spots} onSpotSelect={onSpotSelect} basemap={basemap} mapRef={mapRef} />
      )}

      <BasemapToggle basemap={basemap} setBasemap={setBasemap} />

      <button
        data-testid="danger-toggle"
        onClick={() => setShowDanger(!showDanger)}
        className={`absolute top-[8.25rem] right-4 z-[500] flex items-center gap-2 px-4 py-2 rounded-full text-xs font-heading font-semibold backdrop-blur-xl border shadow-2xl transition-colors ${
          showDanger
            ? "bg-[#ffaa00] text-black border-[#ffaa00]"
            : "bg-black/70 text-zinc-200 border-white/15 hover:border-[#ffaa00]/60 hover:text-[#ffaa00]"
        }`}
      >
        <span className={`w-2.5 h-2.5 rounded-sm ${showDanger ? "bg-black/60" : "bg-[#ffaa00]"}`} />
        Danger zones {showDanger ? "ON" : "OFF"}
      </button>

      <DroneDropChip onDropAt={onDropAt} />

      <header className="absolute top-4 left-4 z-[500] flex items-center gap-3" data-testid="app-header">
        <Link to="/" data-testid="map-home-link" className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl px-4 py-2.5 shadow-2xl flex items-center gap-3 hover:border-[#2ec4b6]/40 transition-colors">
          <img src="/logo.png" alt="wherecaniflymydrone logo" data-testid="map-logo" className="h-9 w-auto shrink-0" />
          <div>
            <p className="font-heading font-bold text-sm text-white leading-none">
              wherecanifly<span className="text-[#2ec4b6]">mydrone</span>
            </p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-300 mt-1">UK CAA Zone Map · CAP 722</p>
            <div className="mt-1.5"><LiveDroneCounter compact /></div>
          </div>
        </Link>
        <Link
          to="/"
          data-testid="map-home-btn"
          className="flex items-center gap-1.5 bg-[#2ec4b6] text-black font-heading font-semibold text-xs px-4 py-2.5 rounded-full hover:bg-[#3dd8ca] transition-colors shadow-2xl"
        >
          <Home size={14} /> Home
        </Link>
      </header>

      <nav className="absolute top-4 right-4 z-[500] hidden lg:block" data-testid="city-nav">
        <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-2xl px-3 py-2 shadow-2xl flex items-center gap-1 flex-wrap max-w-md">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-300 px-1">City Guides</span>
          {CITIES.slice(0, 5).map((c) => (
            <Link
              key={c.slug}
              to={`/can-i-fly-a-drone-in-${c.slug}`}
              data-testid={`city-link-${c.slug}`}
              className="text-xs px-2.5 py-1 rounded-full text-zinc-100 hover:text-[#2ec4b6] hover:bg-white/5 transition-colors"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </nav>

      <Legend weightClass={weightClass} />
      <HotSpots spots={spots} onSelect={onSpotSelect} />

      {/* Desktop panel */}
      <aside className="hidden md:flex absolute top-20 left-4 bottom-6 z-[500] w-[380px] flex-col bg-black/60 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl overflow-hidden" data-testid="ai-panel-desktop">
        <AIPanel {...{ weightClass, setWeightClass, assessment, messages, streaming, input, setInput, onSend, onSearchSelect, onLocate, locating }} />
      </aside>

      {/* Mobile bottom sheet */}
      <div className={`md:hidden absolute left-0 right-0 bottom-0 z-[500] bg-black/80 backdrop-blur-2xl border-t border-white/10 rounded-t-3xl shadow-2xl transition-[height] duration-300 flex flex-col ${sheetOpen ? "h-[58%]" : "h-14"}`} data-testid="ai-panel-mobile">
        <button
          data-testid="sheet-toggle"
          onClick={() => setSheetOpen(!sheetOpen)}
          className="w-full h-14 shrink-0 flex items-center justify-center gap-2 text-zinc-400"
        >
          <span className="w-10 h-1 rounded-full bg-white/20" />
          {sheetOpen ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          <span className="text-xs font-heading">{sheetOpen ? "Hide" : "Maverick AI"}</span>
        </button>
        {sheetOpen && (
          <div className="flex-1 min-h-0">
            <AIPanel {...{ weightClass, setWeightClass, assessment, messages, streaming, input, setInput, onSend, onSearchSelect, onLocate, locating }} />
          </div>
        )}
      </div>
    </div>
  );
}


